import { Alert, Button, Flex, Form, Input, message, Modal, Switch, Spin } from "antd";
import { useForm } from "antd/lib/form/Form";
import React, { useEffect, useRef, useState } from "react";
import { PhoneNumberInput } from "@/components/phone-number-input";
import { confirmFooter } from "@/components/modals/confirmFooter";
import {
  ArrowLeftOutlined,
  CheckOutlined,
  EditOutlined,
  FlagOutlined,
  MailOutlined,
  MergeCellsOutlined,
  PhoneOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { Typography } from "antd";
import dayjs from "dayjs";
import { axiosInstance, displayEmail } from "@/utilities";
import { API_BASE_URL } from "@/services/XCONS_ENV";
import {
  IdentityConflict,
  IdentityConflicts,
  fetchIdentityConflicts,
  readIdentityConflictError,
} from "@/services/user-identity.api";

const { Title, Text, Link } = Typography;

interface Props {
  refresh?: () => void;
  userData?: any;
  closeModal?: () => void;
  // Archivado (Fase 2): contactId + estado actual, para el select "Status".
  contactId?: string | number;
  archived?: boolean;

  isEditing: boolean;
  onBeginEdit: () => void;
  onEndEdit: () => void;

  /**
   * OpenSpec 021 — se pasa sólo cuando el operador PUEDE unificar (flag encendido + Contactos
   * nivel `admin` en el store + contacto sin archivar). Sin esto el choque de identidad se informa
   * como error del campo y nada más: es lo que corresponde, por ejemplo, en el panel de
   * Conversaciones.
   */
  onRequestMerge?: (conflict: IdentityConflict) => void;
  /**
   * Por qué NO se puede unificar, cuando no se puede. Sirve para no mentir en el aviso: la primera
   * versión decía "hace falta permiso de Contactos nivel admin" en todos los casos, incluso cuando
   * el motivo era otro, y mandaba a revisar un permiso que estaba bien.
   */
  mergeUnavailable?: "permission" | "archived";
}

export const ContactData = ({ refresh, userData, closeModal, contactId, archived, isEditing, onBeginEdit, onEndEdit, onRequestMerge, mergeUnavailable }: Props) => {
  const { t } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [hasErrors, setHasErrors] = useState(false);
  const [isActive, setIsActive] = useState(!archived);

  // Pre-fill the form whenever the panel enters edit mode or userData changes.
  // antd Form's `initialValue` only applies on mount; without this hook the
  // form would render empty if userData arrives async or the panel is
  // toggled back into edit mode after a save. Saving with empty fields
  // would wipe the contact's data — see BUG-005.
  useEffect(() => {
    if (!isEditing) return;
    if (!userData) return;
    const active = !archived;
    setIsActive(active);
    form.setFieldsValue({
      firstname: userData.firstname ?? "",
      lastname: userData.lastname ?? "",
      // El placeholder de WhatsApp entra como campo vacío: `handleSubmit` filtra los strings
      // vacíos, así que guardar sin tocarlo NO lo pisa, y tipear el mail real lo reemplaza.
      email: displayEmail(userData.email),
      whatsapp: userData.whatsapp ? String(userData.whatsapp) : "",
      company: userData.company ?? "",
      status: active,
    });
    setHasErrors(false);
  }, [isEditing, userData, archived, form]);

  // ── OpenSpec 021 · conflicto de identidad ────────────────────────────────────────────────────
  //
  // Escribir en esta ficha el e-mail o el WhatsApp de OTRO contacto casi nunca es un typo: es la
  // misma persona con dos cuentas. Antes esto terminaba en un `Error updating user` genérico (el
  // e-mail choca con `UNIQUE (email, website_id)` de `customer_entity`) o, peor, en un guardado
  // silencioso con el WhatsApp duplicado, que deja el login por WhatsApp apuntando a dos cuentas.
  //
  // Ahora se avisa on-blur y, al guardar, se ofrece unificar.
  const [checking, setChecking] = useState(false);
  /** Descarta respuestas viejas si el operador sigue tipeando. */
  const checkSeq = useRef(0);

  const normalizeWhatsapp = (value: unknown) =>
    String(value ?? "")
      .replace(/\D/g, "")
      .replace(/^5415/, "5411")
      .replace(/^549/, "54");

  const normalizeEmail = (value: unknown) =>
    String(value ?? "").trim().toLowerCase();

  /** Sólo se consulta lo que realmente cambió: guardar el propio dato no es un conflicto. */
  const changedIdentityFields = () => {
    const email = form.getFieldValue("email");
    const whatsapp = form.getFieldValue("whatsapp");
    return {
      email:
        normalizeEmail(email) && normalizeEmail(email) !== normalizeEmail(userData?.email)
          ? String(email).trim()
          : undefined,
      whatsapp:
        normalizeWhatsapp(whatsapp) &&
        normalizeWhatsapp(whatsapp) !== normalizeWhatsapp(userData?.whatsapp)
          ? String(whatsapp)
          : undefined,
    };
  };

  const CONFLICT_MESSAGES = {
    email: t("contacts.identity.conflict.email", {
      defaultValue: "Ese e-mail ya lo usa otro contacto",
    }),
    whatsapp: t("contacts.identity.conflict.whatsapp", {
      defaultValue: "Ese teléfono ya lo usa otro contacto",
    }),
  };

  /**
   * El conflicto se pinta como error del campo, pero **no** deshabilita Guardar: es justo al
   * apretar Guardar cuando se ofrece unificar. Por eso `hasErrors` los ignora (ver `isBlocking`).
   */
  const applyConflictErrors = (conflicts: IdentityConflicts) => {
    form.setFields([
      { name: "email", errors: conflicts.email ? [CONFLICT_MESSAGES.email] : [] },
      { name: "whatsapp", errors: conflicts.whatsapp ? [CONFLICT_MESSAGES.whatsapp] : [] },
    ]);
  };

  /** Un error de verdad (formato, requerido). Los de identidad se resuelven con el popup. */
  const isBlocking = (error: string) =>
    error !== CONFLICT_MESSAGES.email && error !== CONFLICT_MESSAGES.whatsapp;

  /**
   * Valor por el que ya se ofreció unificar, por campo. Sin esto el popup vuelve a abrirse cada vez
   * que el campo pierde el foco (basta con hacer click en cualquier otro lado), incluso después de
   * que el operador eligió "Corregir el dato".
   */
  const offeredFor = useRef<{ email?: string; whatsapp?: string }>({});

  const runConflictCheck = async (): Promise<IdentityConflicts> => {
    const fields = changedIdentityFields();
    if (!fields.email && !fields.whatsapp) {
      applyConflictErrors({ email: null, whatsapp: null });
      return { email: null, whatsapp: null };
    }

    const seq = ++checkSeq.current;
    setChecking(true);
    try {
      // `contactId` scopea el chequeo al store de ESTA ficha: es el store en el que está parado el
      // operador y el único en el que puede unificar desde acá.
      const conflicts = await fetchIdentityConflicts(Number(userData?.userId), {
        ...fields,
        contactId,
      });
      // Respuesta vieja: ya salió una consulta más nueva.
      if (seq !== checkSeq.current) return { email: null, whatsapp: null };
      applyConflictErrors(conflicts);
      return conflicts;
    } catch (err) {
      // Que falle el chequeo no puede trabar al operador: el PATCH revalida y devuelve 409.
      console.error("identity conflict check failed", err);
      return { email: null, whatsapp: null };
    } finally {
      if (seq === checkSeq.current) setChecking(false);
    }
  };

  const fieldLabel = (field: "email" | "whatsapp") =>
    field === "email"
      ? t("contacts.identity.field.email", { defaultValue: "e-mail" })
      : t("contacts.identity.field.whatsapp", { defaultValue: "número de WhatsApp" });

  /**
   * El popup de la decisión. Unificar **elimina la cuenta del otro contacto y es irreversible**,
   * así que acá no se ejecuta nada: se abre el drawer de unificación, que exige revisar el plan y
   * confirmar tipeando el nombre.
   */
  const offerMerge = (conflict: IdentityConflict) => {
    const values = {
      field: fieldLabel(conflict.field),
      value: conflict.value,
      who: conflict.name || conflict.email,
    };

    // Cada motivo por el que no se puede unificar manda a hacer algo distinto: cambiar el dato,
    // pedir permiso, o reactivar una ficha. Mezclarlos en un solo texto manda a revisar lo que no
    // es. El motivo del lado del backend viene scopeado al store de esta ficha.
    if (!conflict.canMerge || !onRequestMerge) {
      const reason = !conflict.canMerge
        ? ({
            not_in_store: "notInStore",
            source_archived: "sourceArchived",
            target_archived: "archived",
            no_shared_store: "otherStore",
          }[conflict.blockedReason ?? "no_shared_store"] ?? "otherStore")
        : (mergeUnavailable ?? "permission");
      const content = {
        // El dato lo tiene alguien que NO es contacto de este store. La unificación es por store
        // (D1 revisada) y desde acá sólo se puede unificar dentro del store de esta ficha.
        notInStore: t("contacts.identity.blocked.notInStore", {
          ...values,
          defaultValue:
            "El {{field}} {{value}} lo usa {{who}}, que no tiene ficha en este store. La unificación es dentro de un mismo store, así que acá hay que usar otro dato.",
        }),
        sourceArchived: t("contacts.identity.blocked.sourceArchived", {
          ...values,
          defaultValue:
            "El {{field}} {{value}} lo usa {{who}}, cuya ficha en este store está archivada. Hay que reactivarla para poder unificar los contactos.",
        }),
        otherStore: t("contacts.identity.blocked.otherStore", {
          ...values,
          defaultValue:
            "El {{field}} {{value}} lo usa {{who}}, que no tiene ficha en ningún store en común con este contacto. La unificación es dentro de un mismo store, así que acá hay que usar otro dato.",
        }),
        permission: t("contacts.identity.blocked.noPermission", {
          ...values,
          defaultValue:
            "El {{field}} {{value}} lo usa {{who}}. Para unificar los dos contactos hace falta permiso de Contactos nivel admin en el store.",
        }),
        archived: t("contacts.identity.blocked.archived", {
          ...values,
          defaultValue:
            "El {{field}} {{value}} lo usa {{who}}. Este contacto está archivado: hay que reactivarlo antes de poder unificarlo.",
        }),
      }[reason];

      Modal.warning({
        title: t("contacts.identity.blocked.title", {
          defaultValue: "Ese dato ya está en uso",
        }),
        content,
        okText: t("contacts.identity.blocked.ok", { defaultValue: "Entendido" }),
      });
      return;
    }

    Modal.confirm({
      title: t("contacts.identity.merge.title", {
        defaultValue: "¿Son la misma persona?",
      }),
      width: 520,
      content: (
        <Flex vertical gap={8}>
          <Text>
            {/* Sin lista de stores: el chequeo está scopeado al store de esta ficha, así que el
                único store del que se habla es el que el operador ya tiene abierto. Antes acá se
                listaban todos los stores compartidos ("en mocona, puntoseco") y eso hacía dudar de
                en cuál iba a unificar. */}
            {t("contacts.identity.merge.body", {
              ...values,
              defaultValue: "El {{field}} {{value}} ya está registrado en la ficha de {{who}}.",
            })}
          </Text>
          <Text>
            {t("contacts.identity.merge.explain", {
              ...values,
              defaultValue:
                "Si son la misma persona, los datos y registros de {{who}} pasarán a esta ficha y su cuenta se eliminará.",
            })}
          </Text>
          <Text strong>
            {t("contacts.identity.merge.confirm", {
              ...values,
              defaultValue: "¿Querés unificar estos contactos?",
            })}
          </Text>
        </Flex>
      ),
      okText: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {t("contacts.identity.merge.ok", { defaultValue: "Unificar contactos" })}
          <MergeCellsOutlined />
        </span>
      ),
      okButtonProps: { style: { backgroundColor: "#15744b", borderColor: "#15744b" } },
      cancelText: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <ArrowLeftOutlined />
          {t("contacts.identity.merge.cancel", { defaultValue: "Volver y usar otro dato" })}
        </span>
      ),
      footer: confirmFooter,
      onOk: () => onRequestMerge(conflict),
    });
  };

  /**
   * On-blur: se ofrece unificar **en el acto**. La primera versión sólo pintaba el error y esperaba
   * el Guardar, y eso obliga al operador a intentar guardar algo que ya sabe que no va a entrar.
   * El error del campo se sigue marcando, para que quede claro qué dato hay que cambiar si elige
   * corregirlo.
   */
  const checkAndOffer = async () => {
    const conflicts = await runConflictCheck();
    const conflict = conflicts.email ?? conflicts.whatsapp;
    if (!conflict) return;
    // Un mismo valor se ofrece una sola vez; el Guardar sí vuelve a preguntar siempre.
    if (offeredFor.current[conflict.field] === conflict.value) return;
    offeredFor.current[conflict.field] = conflict.value;
    offerMerge(conflict);
  };

  const handleSubmit = async () => {
    const values = form.getFieldsValue([
      "email",
      "firstname",
      "lastname",
      "company",
      "whatsapp",
    ]);

    const filteredValues = Object.entries(values).reduce((acc, [key, value]) => {
      if (value !== null && value !== undefined && value !== "null" && value !== "") {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    // El chequeo se repite acá aunque el blur ya lo haya corrido: entre un blur y el guardado el
    // operador pudo cambiar el valor de nuevo, o otro operador pudo quedarse con el dato.
    const conflicts = await runConflictCheck();
    const conflict = conflicts.email ?? conflicts.whatsapp;
    if (conflict) {
      // Acá se ofrece siempre, incluso si el blur ya lo había ofrecido para este mismo valor: el
      // operador está pidiendo guardar, no se lo puede dejar pasar en silencio.
      offerMerge(conflict);
      return;
    }

    try {
      setLoading(true);
      // OpenSpec 024 — el nombre y el apellido son POR STORE, así que el PATCH tiene que decir DE
      // QUÉ FICHA son. `/users/:id` es un endpoint de identidad: sin `contactId` el backend sólo
      // puede resolver la ficha cuando la identidad tiene una sola, y con varias contesta
      // 409 NAME_SCOPE_REQUIRED en vez de renombrar a la persona en todos los stores.
      //
      // La Empresa se sumó al nombre: también es POR STORE, y por eso viaja en el mismo PATCH con
      // el `contactId`. Este comentario decía que era de la identidad — era cierto hasta que se
      // descubrió que en realidad se guardaba en una fila huérfana del EAV de Magento, colgada de
      // un atributo que ni siquiera es del customer.
      //
      // El e-mail y el WhatsApp sí son de la identidad y se siguen guardando igual: son las claves
      // con las que la persona entra, no pueden diferir por store.
      await axiosInstance.patch(
        `${API_BASE_URL}/users/${userData?.userId}`,
        { ...filteredValues, contactId },
      );

      // Archivado (Fase 2): el select Status archiva/desarchiva el contacto en
      // su store vía PATCH /contacts/{contactId}. Solo si cambió el estado.
      const desiredArchived = form.getFieldValue("status") === false;
      if (contactId != null && desiredArchived !== !!archived) {
        await axiosInstance.patch(`${API_BASE_URL}/contacts/${contactId}`, {
          archived: desiredArchived,
        });
      }

      message.success(t("user.update.success"));
      onEndEdit();
      closeModal?.();
      refresh?.();
    } catch (err) {
      // 409 del backend: el chequeo previo quedó viejo (carrera con otro operador) o alguien llamó
      // al endpoint directo. Se trata igual que el conflicto detectado a tiempo.
      const late = readIdentityConflictError(err);
      if (late) {
        applyConflictErrors(late);
        // El `PATCH` es a nivel identidad y no sabe desde qué ficha se está editando, así que su
        // payload trae el par de cualquier store compartido. Para ofrecer unificar se vuelve a
        // preguntar scopeado a esta ficha; si el re-chequeo no ve nada (el otro operador deshizo lo
        // suyo entre medio) queda el error del campo y nada más.
        const scoped = await runConflictCheck();
        const lateConflict = scoped.email ?? scoped.whatsapp;
        if (lateConflict) offerMerge(lateConflict);
        return;
      }
      console.error("Error updating user", err);
      message.error("Error updating user");
    } finally {
      setLoading(false);
    }
  };

  if (isEditing) {
    return (
      <div style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        <div style={{ flex: 1, overflowY: "auto" }}>
          <Spin spinning={loading}>
            <Form
              layout="vertical"
              form={form}
              onFieldsChange={() => {
                const blocking = form
                  .getFieldsError()
                  .some(({ errors }) => errors.some(isBlocking));
                setHasErrors(blocking);
              }}
            >
              <div style={{ padding: "16px 24px 0", display: "flex", flexDirection: "column", gap: 24 }}>
                <Alert
                  icon={<WarningFilled />}
                  message={t("alert.warning.contactData")}
                  type="warning"
                  showIcon
                  style={{ fontSize: 14 }}
                />
                <div>
                  <Flex gap={8}>
                    <Form.Item
                      label="Nombre"
                      name="firstname"
                      initialValue={userData?.firstname}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label="Apellido"
                      name="lastname"
                      initialValue={userData?.lastname}
                      style={{ flex: 1, minWidth: 0 }}
                    >
                      <Input />
                    </Form.Item>
                  </Flex>

                  <Form.Item label="Email" name="email" initialValue={displayEmail(userData?.email)}>
                    <Input onBlur={() => void checkAndOffer()} />
                  </Form.Item>

                  <Form.Item
                    label="Número de teléfono"
                    name="whatsapp"
                    initialValue={String(userData?.whatsapp)}
                    rules={[
                      {
                        required: true,
                        message: t("contacts.address.validation.whatsapprequired"),
                      },
                      {
                        validator: (_, value) => {
                          const phoneDigits = value?.replace(/\D/g, "");
                          if (!phoneDigits || phoneDigits.length < 11) {
                            return Promise.reject(
                              t("contacts.address.validation.whatsapplength"),
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <PhoneNumberInput onBlur={() => void checkAndOffer()} />
                  </Form.Item>

                  <Form.Item
                    label="Empresa"
                    name="company"
                    initialValue={userData?.company}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item label="Estado" style={{ marginBottom: 0 }}>
                    <Flex align="center" gap={8}>
                      <Form.Item name="status" valuePropName="checked" noStyle>
                        <Switch onChange={setIsActive} />
                      </Form.Item>
                      <span>{isActive ? "Activo" : "Archivado"}</span>
                    </Flex>
                  </Form.Item>
                </div>
              </div>
            </Form>
          </Spin>
        </div>

        <Flex
          justify="end"
          gap={8}
          style={{
            flexShrink: 0,
            backgroundColor: "#fff",
            padding: "12px 24px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Button onClick={onEndEdit}>
            {t("buttons.cancel")}
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            icon={<CheckOutlined />} iconPosition="end"
            loading={checking}
            disabled={hasErrors || checking}
          >
            {t("buttons.save")}
          </Button>
        </Flex>
      </div>
    );
  }

  return (
    <Flex vertical gap={8}>
      <Text strong>{`${userData?.firstname} ${userData?.lastname}`}</Text>
      <Text type="secondary">
        {t("contacts.label.createdaccount")} {dayjs(userData?.createdAt).format("DD/MM/YY hh:mm a")}
      </Text>

      <Flex vertical gap={8} style={{ marginTop: 28 }}>
        <Flex align="center" gap={10}>
          <MailOutlined />
          <Text>{userData?.email ?? "-"}</Text>
        </Flex>

        <Flex align="center" gap={10}>
          <PhoneOutlined />
          <Text>{userData?.whatsapp ?? "-"}</Text>
        </Flex>

        <Flex align="center" gap={10}>
          <FlagOutlined />
          <Text>{userData?.company ?? "-"}</Text>
        </Flex>
      </Flex>

      <Flex>
        <Button
          size="small"
          shape="round"
          icon={<EditOutlined />}
          iconPosition="end"
          style={{ marginTop: 22, width: 68 }}
          onClick={onBeginEdit}
        >
          Editar
        </Button>
      </Flex>
    </Flex>
  );
};
